import React, { useEffect, useState, useMemo } from "react";
import { MessageCircle, Search, UserCheck, ShieldAlert, X, ArrowLeft, MoreVertical, Filter } from "lucide-react";
import { api } from "../lib/api";
import { useInboxSocket } from "../lib/useInboxSocket";
import MessageThread from "../components/MessageThread";
import { toast } from "sonner";

/**
 * WhatsApp-Web style Shared Inbox.
 * - Desktop: 2-column (sidebar + chat); contact panel slides in from right when toggled.
 * - Mobile: single pane — sidebar OR chat (back button returns to sidebar).
 */
export default function AdminInbox() {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [search, setSearch] = useState("");
  const [designers, setDesigners] = useState([]);
  const [showContact, setShowContact] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const { liveMessage, connected, liveConversation, liveTyping, sendTyping } = useInboxSocket(true);

  const load = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set("status", filterStatus);
    if (filterAssigned) params.set("assigned", filterAssigned);
    if (search) params.set("search", search);
    api.get(`/admin/inbox?${params.toString()}`).then(r => {
      setConvs(r.data || []);
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterStatus, filterAssigned, search]);
  useEffect(() => { api.get("/admin/designers").then(r => setDesigners(r.data)); }, []);

  useEffect(() => {
    if (!liveConversation) return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveConversation]);

  const active = useMemo(() => convs.find(c => c.id === activeId), [convs, activeId]);

  const assign = async (designerId) => {
    try {
      await api.post(`/admin/inbox/${activeId}/assign`, { designer_id: designerId });
      toast.success("Designer assigned");
      setAssignModal(false);
      load();
    } catch { toast.error("Assign failed"); }
  };

  const takeover = async () => {
    if (!window.confirm("Take over this conversation? The currently assigned designer will be unassigned.")) return;
    try {
      await api.post(`/admin/inbox/${activeId}/takeover`);
      toast.success("You're now handling this conversation");
      load();
    } catch { toast.error("Takeover failed"); }
  };

  const closeConv = async () => {
    if (!window.confirm("Close this conversation?")) return;
    try {
      await api.post(`/admin/inbox/${activeId}/close`);
      toast.success("Closed");
      load();
    } catch { toast.error("Close failed"); }
  };

  return (
    <div className="md:space-y-3 -mx-4 -my-4 md:mx-0 md:my-0">
      <div className="hidden md:flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Shared Inbox</h2>
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-amber-400"}`} />
          {connected ? "Live" : "Reconnecting..."}
        </div>
      </div>

      <div className="grid md:grid-cols-[340px_minmax(0,1fr)] md:rounded-2xl md:border md:border-[#1a3c5e]/10 overflow-hidden h-[calc(100vh-7rem)] md:h-[78vh]">
        {/* LEFT: Conversation sidebar */}
        <div className={`bg-white border-r border-[#1a3c5e]/10 flex flex-col ${active ? "hidden md:flex" : "flex"}`}>
          <div className="px-3 pt-3 pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2 md:hidden">
              <h2 className="font-display text-lg font-bold text-[#1a3c5e]">Inbox</h2>
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-400"}`} />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone, message..."
                data-testid="admin-inbox-search"
                className="w-full pl-9 pr-9 py-2 text-sm bg-[#f0f2f5] border border-transparent rounded-full focus:outline-none focus:border-[#F7941D] focus:bg-white"
              />
              <button onClick={() => setShowFilters(!showFilters)} className="absolute right-2 top-1.5 p-1 rounded-full hover:bg-black/5" data-testid="admin-inbox-filter-toggle">
                <Filter className={`w-4 h-4 ${(filterStatus || filterAssigned) ? "text-[#F7941D]" : "text-gray-500"}`} />
              </button>
            </div>
            {showFilters && (
              <div className="flex gap-2 mt-2">
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="">All status</option>
                  <option value="open">Open</option>
                  <option value="human_handled">Active</option>
                  <option value="closed">Closed</option>
                </select>
                <select value={filterAssigned} onChange={(e) => setFilterAssigned(e.target.value)} className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="">All assignment</option>
                  <option value="unassigned">Unassigned</option>
                  {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="admin-conv-list">
            {convs.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">No conversations.</div>
            ) : convs.map(c => <ConvRow key={c.id} c={c} active={activeId === c.id} onClick={() => setActiveId(c.id)} liveTyping={liveTyping && liveTyping.conversation_id === c.id && liveTyping.active ? liveTyping : null} />)}
          </div>
        </div>

        {/* RIGHT: Chat pane */}
        <div className={`bg-[#efeae2] flex-col ${active ? "flex" : "hidden md:flex"}`}>
          {active ? (
            <MessageThread
              conversationId={active.id}
              role="admin"
              apiBase={`/admin/inbox/${active.id}`}
              canInternalNote={true}
              liveMessage={liveMessage}
              liveTyping={liveTyping}
              sendTyping={sendTyping}
              onUnreadChange={load}
              header={
                <div className="px-3 py-2.5 bg-[#f0f2f5] border-b border-black/10 flex items-center gap-2 flex-wrap">
                  <button onClick={() => setActiveId(null)} className="md:hidden p-1 rounded-full hover:bg-black/5" data-testid="admin-thread-back">
                    <ArrowLeft className="w-5 h-5 text-[#54656f]" />
                  </button>
                  <Avatar name={active.title} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#111b21] text-sm truncate" data-testid="admin-thread-title">{active.title}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {active.subtitle}
                      {liveTyping && liveTyping.conversation_id === active.id && liveTyping.active && <span className="ml-1 text-[#06cf9c] font-semibold"> · typing...</span>}
                    </div>
                  </div>
                  <button onClick={() => setShowContact(!showContact)} data-testid="admin-toggle-contact" className="p-1.5 rounded-full hover:bg-black/5"><UserCheck className="w-4 h-4 text-[#54656f]" /></button>
                  <div className="hidden sm:flex items-center gap-1.5 ml-1">
                    <button onClick={() => setAssignModal(true)} data-testid="admin-assign-btn" className="px-2.5 py-1 text-xs bg-[#1a3c5e] hover:bg-[#122a42] text-white font-semibold rounded-lg flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" />{active.assigned_designer_id ? "Reassign" : "Assign"}</button>
                    {active.assigned_designer_id && <button onClick={takeover} data-testid="admin-takeover-btn" className="px-2.5 py-1 text-xs bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" />Take Over</button>}
                    <button onClick={closeConv} className="px-2.5 py-1 text-xs border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg">Close</button>
                  </div>
                </div>
              }
            />
          ) : (
            <div className="hidden md:flex flex-col items-center justify-center text-gray-400 text-sm h-full bg-[#f8f9fa]">
              <MessageCircle className="w-16 h-16 text-gray-300" />
              <div className="mt-3 font-display text-lg">Select a conversation to start</div>
              <div className="text-xs mt-1">All your customer conversations in one place</div>
            </div>
          )}
        </div>
      </div>

      {/* Contact panel (drawer) */}
      {showContact && active && (
        <div className="fixed inset-0 z-40 bg-black/40 md:bg-black/20" onClick={() => setShowContact(false)} data-testid="admin-contact-panel-overlay">
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-contact-panel">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-[#1a3c5e]">Customer Details</h3>
              <button onClick={() => setShowContact(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-col items-center gap-2">
                <Avatar name={active.title} size="xl" />
                <div className="font-display font-bold text-[#1a3c5e] text-lg">{active.customer?.name || active.title}</div>
              </div>
              {active.customer && (
                <div className="text-sm space-y-2">
                  <Row label="Email" value={active.customer.email} />
                  <Row label="Phone" value={active.customer.phone} />
                  <Row label="City" value={active.customer.city} />
                  <Row label="State" value={active.customer.state} />
                </div>
              )}
              {active.designer && (
                <div className="text-sm space-y-1 pt-3 border-t border-gray-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Assigned Designer</div>
                  <div className="font-semibold text-[#1a3c5e]">{active.designer.name}</div>
                  <div className="text-xs text-gray-500">{active.designer.email} · {active.designer.phone}</div>
                </div>
              )}
              {active.order_id && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Current Order</div>
                  <a href={`/admin/orders`} className="text-sm font-mono text-[#F7941D] hover:underline">#{active.order_id.slice(0,8)}</a>
                </div>
              )}
              <div className="pt-4 border-t border-gray-100 sm:hidden space-y-2">
                <button onClick={() => { setAssignModal(true); setShowContact(false); }} className="w-full px-3 py-2 bg-[#1a3c5e] text-white font-semibold rounded-lg text-sm">Assign Designer</button>
                {active.assigned_designer_id && <button onClick={() => { takeover(); setShowContact(false); }} className="w-full px-3 py-2 bg-amber-500 text-white font-semibold rounded-lg text-sm">Take Over</button>}
                <button onClick={() => { closeConv(); setShowContact(false); }} className="w-full px-3 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg text-sm">Close Conversation</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assignModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAssignModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display text-lg font-bold text-[#1a3c5e]">Assign Designer</h3>
              <button onClick={() => setAssignModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {designers.map(d => (
                <button key={d.id} onClick={() => assign(d.id)} data-testid={`assign-conv-designer-${d.id}`} className="w-full text-left p-3 rounded-xl border border-[#1a3c5e]/10 hover:bg-[#FEF3E2]/40 flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-[#1a3c5e]">{d.name}</div>
                    <div className="text-xs text-gray-500">{d.profile?.skills?.join(", ") || "—"}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-[#FEF3E2] text-[#F7941D] font-bold">{d.active_orders} active</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConvRow({ c, active, onClick, liveTyping }) {
  const time = c.last_message_at ? new Date(c.last_message_at) : null;
  const timeLabel = time ? formatRelativeTime(time) : "";
  return (
    <button
      onClick={onClick}
      data-testid={`admin-conv-${c.id}`}
      className={`w-full text-left px-3 py-3 border-b border-gray-50 hover:bg-[#f5f6f6] transition-colors flex items-start gap-3 ${active ? "bg-[#e8eef1]" : ""}`}
    >
      <Avatar name={c.title} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center gap-2">
          <div className="font-semibold text-[#111b21] text-sm truncate">{c.title}</div>
          <div className={`text-[11px] flex-shrink-0 ${c.unread > 0 ? "text-[#06cf9c] font-bold" : "text-gray-400"}`}>{timeLabel}</div>
        </div>
        <div className="flex justify-between items-center mt-0.5 gap-2">
          <div className="text-xs text-gray-500 truncate flex-1">
            {liveTyping ? <span className="text-[#06cf9c] italic font-semibold">typing...</span> : (c.last_message_preview || "—")}
          </div>
          {c.unread > 0 && <span className="bg-[#06cf9c] text-white text-[10px] font-bold rounded-full px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center">{c.unread > 99 ? "99+" : c.unread}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          {c.designer ? <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">→ {c.designer.name}</span> : <span className="text-[9px] uppercase tracking-wider text-amber-600 font-bold">UNASSIGNED</span>}
          {c.status !== "open" && c.status !== "human_handled" && <span className="text-[9px] text-gray-400">· {c.status}</span>}
        </div>
      </div>
    </button>
  );
}

const AVATAR_COLORS = ["#F7941D", "#1a3c5e", "#D32F2F", "#0EA5E9", "#10b981", "#8b5cf6", "#06b6d4", "#f59e0b", "#ec4899"];
function pickColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function Avatar({ name, size = "md" }) {
  const initials = (name || "C").trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const px = { sm: "w-7 h-7 text-[10px]", md: "w-10 h-10 text-sm", lg: "w-12 h-12 text-base", xl: "w-16 h-16 text-xl" }[size] || "w-10 h-10";
  return (
    <div
      className={`${px} rounded-full flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: pickColor(name) }}
    >{initials}</div>
  );
}

function formatRelativeTime(d) {
  const now = new Date();
  const dt = (now.getTime() - d.getTime()) / 1000;
  if (dt < 60) return "now";
  if (now.toDateString() === d.toDateString()) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const yest = new Date(now.getTime() - 86400000);
  if (yest.toDateString() === d.toDateString()) return "Yesterday";
  if (dt < 7 * 86400) return d.toLocaleDateString("en-IN", { weekday: "short" });
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3">
      <div className="text-xs text-gray-500 w-16 flex-shrink-0">{label}</div>
      <div className="text-xs font-semibold text-[#1a3c5e] break-all">{value || "—"}</div>
    </div>
  );
}
